/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import {
  Banknote,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Trash2,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/Alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import TablesModal from "./TablesModal";
import { orderService } from "../../services/OrderService";
import LoadingSpinner from "../common/LoadingSpinner";
import { toast } from "react-hot-toast";
import { getPrinterService } from "../../services/printer/MockPrinterService";

const OrdersTable = ({
  orders,
  getTotal,
  handlePayment,
  selectedTable,
  onTableSelect,
  onOrdersUpdate,
}) => {
  const printerService = getPrinterService();
  const [showTableModal, setShowTableModal] = useState(false);
  const [error, setError] = useState("");
  const [tableOrders, setTableOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    orderId: null,
    productId: null,
    isLastProduct: false,
  });

  useEffect(() => {
    const loadOrders = async () => {
      if (!selectedTable) return;
      setLoading(true);

      try {
        const result = await orderService.listOrders();

        if (result.success) {
          const allOrders = orderService.formatOrders(result.data);
          const filteredOrders = allOrders.filter(
            (order) => order.table === selectedTable.description
          );
          setTableOrders(filteredOrders);
          console.log("ORDENES ", tableOrders)
        }
      } catch (error) {
        setError("Error al cargar las órdenes");
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [selectedTable]);

  const handleDeleteClick = (orderId, productName, products) => {
    setDeleteDialog({
      isOpen: true,
      orderId,
      productName,
      isLastProduct: products.length === 1,
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const { orderId, productName, isLastProduct } = deleteDialog;

      const order = orders.find((o) => o._id === orderId);
      if (!order) {
        throw new Error("Orden no encontrada");
      }

      const cancelledProduct = order.products.find(
        (p) => p.name === productName
      );
      if (!cancelledProduct) {
        throw new Error("Producto no encontrado");
      }

      const cancellationData = {
        ...cancelledProduct,
        table: order.table,
        orderId: order._id,
        selectedAddon: cancelledProduct.selectedAddon,
      };

      const printers = printerService.getAllPrinters();
      console.log("Impresoras disponibles para cancelación:", printers);

      if (printers.length === 0) {
        console.warn("No hay impresoras disponibles para la cancelación");
      } else {
        try {
          await printerService.printCancellationTicket(
            orderId,
            cancellationData,
            printers[0].id
          );
          console.log("Ticket de cancelación impreso exitosamente");
        } catch (printError) {
          console.error("Error al imprimir ticket de cancelación:", printError);
          toast.error("Error al imprimir el ticket de cancelación");
        }
      }

      if (isLastProduct) {
        await orderService.deleteOrder(orderId);
      } else {
        await orderService.deleteProduct(orderId, productName);
      }

      await onOrdersUpdate();
      toast.success(
        isLastProduct
          ? "Orden eliminada correctamente"
          : "Producto eliminado correctamente"
      );
    } catch (error) {
      console.error("Error al eliminar:", error);
      toast.error(`Error: ${error.message}`);
      setError(`Error al eliminar: ${error.message}`);
    } finally {
      setLoading(false);
      setDeleteDialog({
        isOpen: false,
        orderId: null,
        productName: null,
        isLastProduct: false,
      });
    }
  };

  const handlePaymentProcess = async (orderId, method) => {
    try {
      console.log("Iniciando proceso de pago:", { orderId, method });
      await handlePayment(orderId, method);
    } catch (error) {
      console.error("Error procesando pago:", error);
      toast.error(`Error al procesar el pago: ${error.message}`);
    }
  };

  const renderPaymentButton = (order, type) => {
    const isPagoSolicitado = order.status === "PagoSolicitado";
    const isThisPaymentType =
      order.payment?.toLowerCase() === type.toLowerCase();

    if (isPagoSolicitado && isThisPaymentType) {
      return (
        <div className="bg-orange-100 border-2 border-orange-500 text-orange-700 px-4 py-2 rounded flex items-center">
          {type === "Efectivo" ? (
            <Banknote className="mr-2 h-4 w-4" />
          ) : (
            <CreditCard className="mr-2 h-4 w-4" />
          )}
          <span>Pago Solicitado {type}</span>
        </div>
      );
    }

    if (isPagoSolicitado) {
      return null;
    }

    return (
      <button
        className="bg-orange-500 text-white px-3 py-1.5 rounded flex items-center hover:bg-orange-600 transition-colors"
        onClick={() => handlePaymentProcess(order._id, type)}
      >
        {type === "Efectivo" ? (
          <Banknote className="mr-1.5 h-4 w-4" />
        ) : (
          <CreditCard className="mr-1.5 h-4 w-4" />
        )}
        <span className="hidden md:inline">{type}</span>
      </button>
    );
  };

  const renderPaymentSection = (order) => {
    if (order.status === "ProcesandoPago") {
      return (
        <div className="inline-flex items-center justify-center bg-green-100 text-green-800 px-4 py-2 rounded-md">
          <CheckCircle className="mr-2 h-4 w-4" />
          <span>Pago {order.payment} realizado</span>
        </div>
      );
    }

    if (order.status === "PagoSolicitado") {
      return renderPaymentButton(order, order.payment);
    }

    return (
      <div className="flex justify-center gap-2">
        {renderPaymentButton(order, "Efectivo")}
        {renderPaymentButton(order, "Tarjeta")}
      </div>
    );
  };

  const displayOrders = selectedTable ? tableOrders : orders;

  const renderProducts = (order) => {
    return (
      <ul className="list-disc list-inside space-y-1">
        {order.products.map((product, idx) => {
          const isPaid =
            order.payment === "Tarjeta" || order.payment === "Efectivo";

          return (
            <li
              key={idx}
              className="text-gray-700 flex items-center justify-between"
            >
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{product.name}</span>
                  {product.selectedAddon && (
                    <span className="text-sm text-orange-500">
                      ({product.selectedAddon})
                    </span>
                  )}
                </div>
                <span className="text-gray-500">
                  {product.quantity > 1 && ` x${product.quantity}`} - $
                  {product.total}
                </span>
              </div>
              {!isPaid && (
                <button
                  onClick={() =>
                    handleDeleteClick(order._id, product.name, order.products)
                  }
                  className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                  title="Eliminar producto"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md">
      {error && (
        <Alert variant="destructive" className="m-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-between items-center px-6 py-4 border-b">
        <h2 className="text-xl font-semibold flex items-center">
          Órdenes en Curso
          {selectedTable && (
            <span className="ml-2 px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
              {selectedTable.description}
            </span>
          )}
        </h2>
        <button
          className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition-colors"
          onClick={() => setShowTableModal(true)}
        >
          {selectedTable ? "Cambiar Mesa" : "Seleccionar Mesa"}
        </button>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="overflow-x-auto">
          {displayOrders.length > 0 ? (
            <table className="w-full min-w-[600px]">
              <thead className="border-b bg-orange-50">
                <tr>
                  <th className="px-4 py-3 text-left">Fecha</th>
                  <th className="px-4 py-3 text-left">Productos</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-center">Mesa</th>
                  <th className="px-4 py-3 text-center">Pago</th>
                </tr>
              </thead>
              <tbody>
                {displayOrders.map((order) => (
                  <tr key={order.id} className="border-b hover:bg-orange-50">
                    <td className="px-4 py-3">{order.date}</td>
                    <td className="px-4 py-3">{renderProducts(order)}</td>
                    <td className="px-4 py-3 text-right font-medium">
                      ${getTotal(order.products)}
                    </td>
                    <td className="px-4 py-3 text-center">{order.table}</td>
                    <td className="px-4 py-3">{renderPaymentSection(order)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {selectedTable
                ? "No hay órdenes para esta mesa"
                : "Seleccione una mesa para ver sus órdenes"}
            </div>
          )}
        </div>
      )}
      <AlertDialog
        open={deleteDialog.isOpen}
        onOpenChange={(isOpen) =>
          setDeleteDialog((prev) => ({ ...prev, isOpen }))
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Confirmar eliminación
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog.isLastProduct
                ? "Esta acción eliminará la orden completa ya que es el último producto. ¿Desea continuar?"
                : "¿Está seguro que desea eliminar este producto de la orden?"}
              <div className="mt-2 text-orange-600">
                Nota: Se imprimirá una orden de cancelación en cocina/barra
                según corresponda.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-500 hover:bg-red-600"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <TablesModal
        open={showTableModal}
        onOpenChange={setShowTableModal}
        onSelectTable={onTableSelect}
      />
    </div>
  );
};

export default OrdersTable;
